import { useQuery } from '@tanstack/react-query';
import { UserApi } from '@/services/api';
import { useAuth } from '@/auth/AuthProvider';
import { useRef, useCallback } from 'react';

export interface User {
  id: string;
  username: string;
  email: string;
  email_verified: boolean;
  first_name?: string;
  last_name?: string;
  bio?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch and manage user data
 */
export const useUser = () => {
  const { isAuthenticated } = useAuth();
  const refreshInProgressRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);
  
  const queryInfo = useQuery({
    queryKey: ['userData'],
    queryFn: async () => {
      console.log('Fetching user data...');
      try {
        const result = await UserApi.getCurrentUser();
        console.log('User data fetched successfully');
        return result;
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        throw err;
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data for 10 minutes
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch when window gets focus
    refetchOnMount: true, // Only refetch on mount if data is stale
    refetchOnReconnect: false // Prevent refetching on network reconnect
  });

  // Destructure after the hook call
  const {
    data: userData,
    isLoading,
    isError,
    error,
    refetch
  } = queryInfo;

  // Safer refresh function with proper debouncing to prevent API flooding
  const refreshUserData = useCallback(async () => {
    // Clear any existing timer
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    // If a refresh is already in progress, don't start another one
    if (refreshInProgressRef.current) {
      console.log('Refresh already in progress, skipping');
      return;
    }
    
    // Set a small delay before actual refresh to debounce multiple calls
    refreshTimerRef.current = window.setTimeout(() => {
      const doRefresh = async () => {
        refreshInProgressRef.current = true;
        console.log('Manually refreshing user data...');
        
        try {
          await refetch();
        } catch (err) {
          console.error('Error refreshing user data:', err);
        } finally {
          // Reset the flag after completion
          refreshInProgressRef.current = false;
          refreshTimerRef.current = null;
        }
      };
      
      doRefresh();
    }, 200); // 200ms debounce
  }, [refetch]);

  return {
    userData,
    isLoading,
    isError,
    error,
    refreshUserData
  };
}; 